import { Button, Modal, ModalContent } from '@benzinga/core-ui';
import { RenderUpsellCreditCards } from './CreditCardList';
import styled from 'styled-components';
import React from 'react';
import { Card, Plan, ShopManager } from '@benzinga/shop-manager';
import { SessionContext } from '@benzinga/session-context';
import { OneClickUpSellProps } from './types';
import { TrackingManager } from '@benzinga/tracking-manager';
import { RenderUpsellPlans } from './Plans';
import { LoggingManager } from '@benzinga/session';

export const OneClickUpSellModal: React.FC<OneClickUpSellProps> = props => {
  const [selectedCard, setSelectedCard] = React.useState<Card>();
  const [selectedPlan, setSelectedPlan] = React.useState<Plan>();
  const [creditCards, setCreditCards] = React.useState<Card[]>();
  const session = React.useContext(SessionContext);
  const shopManager = session.getManager(ShopManager);
  const logger = session.getManager(LoggingManager);
  const [isLoading, setIsLoading] = React.useState(false);

  const updateCreditCards = React.useCallback(async () => {
    const creditCards = await session.getManager(ShopManager).getCreditCards();
    if (creditCards.err) return;
    setCreditCards(creditCards.ok);
  }, [session]);

  const getErrorMessage = React.useCallback(async (error: any, last4: string | undefined) => {
    if (error.data && typeof error.data === 'object' && 'json' in error.data) {
      const jsonError = await (error.data as { json: () => Promise<unknown> }).json();
      return jsonError && typeof jsonError === 'object' && 'token' in jsonError && Array.isArray(jsonError.token)
        ? jsonError.token[0].split('Error')[0]
        : `There was an issue processing the request for the card ending in ${last4}. The card was (declined/has expired). Please update the card on file and try again later.`;
    }
    return error.message || 'Unable to process request. Please try again later or contact support.';
  }, []);

  const handleSubscriptionForUOA = React.useCallback(async () => {
    const setShowModal = props.setShowModal;
    if (!creditCards?.length) {
      logger.log(
        'error',
        { category: 'checkout-error', message: 'Please add a card in your account billing section' },
        ['toast'],
      );
      return;
    }
    try {
      session.getManager(TrackingManager).trackModalEvent('submit', {
        modal_id: selectedPlan?.slug ?? 'upsell_modal',
        modal_type: 'upsell',
      });

      setIsLoading(true);
      const checkoutResponse = await shopManager.checkout(
        [],
        selectedPlan?.slug ?? '',
        '',
        undefined,
        creditCards[0].uuid,
      );

      if (checkoutResponse.err) {
        const errorMessage = await getErrorMessage(checkoutResponse.err, selectedCard?.last4);
        logger.log(
          'error',
          {
            category: 'checkout-error',
            message: errorMessage,
          },
          ['toast'],
        );
        setIsLoading(false);
        setShowModal(false);
        return;
      }
      setIsLoading(false);
      setShowModal(false);
      logger.log(
        'info',
        {
          category: 'checkout-success',
          message:
            props.messages?.successMsg ??
            'Options Activity was successfully added to your account! Please refresh Pro to see the latest changes.',
        },
        ['toast'],
      );
    } catch (_error) {
      setIsLoading(false);
      setShowModal(false);
      logger.log(
        'error',
        {
          category: 'checkout-error',
          message:
            props.messages?.errorMsg ??
            'It looks like there was an issue processing the request. Please try again later or contact our support team.',
        },
        ['toast'],
      );
    }
  }, [
    props.setShowModal,
    props.messages?.successMsg,
    props.messages?.errorMsg,
    creditCards,
    session,
    selectedPlan?.slug,
    shopManager,
    getErrorMessage,
    selectedCard?.last4,
    logger,
  ]);

  const handlePlanSelection = React.useCallback(
    async (plan: Plan) =>
      setSelectedPlan(
        (await shopManager.getProduct(props.product ?? 'benzinga-pro')).ok?.plans.find(p => p.slug === plan.slug),
      ),
    [props.product, shopManager],
  );

  const handleCreditCardSelection = React.useCallback((card: Card) => setSelectedCard(card), []);

  const closeUpSellModal = React.useCallback(() => {
    const setShowModal = props.setShowModal;
    setShowModal(false);
    session.getManager(TrackingManager).trackModalEvent('close', {
      modal_id: selectedPlan?.slug ?? 'upsell_modal',
      modal_type: 'upsell',
    });
  }, [selectedPlan?.slug, props.setShowModal, session]);

  const init = React.useCallback(async () => {
    session.getManager(TrackingManager).trackModalEvent('view', {
      modal_id: selectedPlan?.slug ?? 'upsell_modal',
      modal_type: 'upsell',
    });
    updateCreditCards();

    //set default plan
    if (props.plans.length) {
      const product = await shopManager.getProduct(props.product);
      if (product.ok) {
        setSelectedPlan(product.ok.plans.find(plan => plan.slug === props.plans[0].slug));
      }
    }
  }, [props.plans, props.product, selectedPlan?.slug, session, shopManager, updateCreditCards]);

  React.useEffect(() => {
    props.show && init();
  }, [init, props.show]);

  return (
    <div>
      {props.show && (
        <Modal
          onClose={closeUpSellModal}
          size={{
            alert: true,
          }}
          title={props.title}
        >
          <StyledModalContent>
            <RenderUpsellPlans
              handlePlanSelection={handlePlanSelection}
              plans={props.plans}
              product={props.product}
              selectedPlan={selectedPlan}
            />

            <>
              <RenderUpsellCreditCards
                creditCards={creditCards}
                handleCreditCardSelection={handleCreditCardSelection}
                selectedCard={selectedCard}
              />
              <UpsellHeader>
                {props.upSellHeader ?? (
                  <>
                    Your card ending in {selectedCard?.last4} will be charged <b>${selectedPlan?.price ?? 0}</b> now,
                    and your subscription will renew for <b>${selectedPlan?.price ?? 0}</b> each{' '}
                    {selectedPlan?.interval ?? 'month'}.
                  </>
                )}
              </UpsellHeader>
            </>
          </StyledModalContent>
          <ModalFooter>
            <StyledButton onClick={closeUpSellModal}>Close</StyledButton>
            <StyledButton disabled={isLoading || !selectedPlan} onClick={handleSubscriptionForUOA}>
              {props.buttonText ?? 'SUBMIT'}
            </StyledButton>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

const UpsellHeader = styled.span`
  margin-bottom: 1em;
`;
const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin: 6px 1em;
`;

const StyledButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.brand};
  color: ${({ theme }) => theme.colors.foregroundActive};
  &:disabled {
    opacity: 0.5;
    color: ${({ theme }) => theme.colors.foregroundActive};
    cursor: not-allowed;
  }
`;

const StyledModalContent = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;
